import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { loginStart, loginSuccess, loginFailure } from '../redux/slices/authSlice';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { apiHelper } from '../utils/api';
import { API_ENDPOINTS, MIVENT_TUTORIAL_URL } from '../utils/constants';
import './Register.scss';
import { showToast } from '../utils/toast';
import { handleGoogleAuth } from '../utils/googleAuth';
import { User, Mail, UserPlus } from 'lucide-react';
import TermsModal from '../components/TandC/TermsModal';
import SessionManagerModal from '../components/SessionManagerModal/SessionManagerModal';

const buildAppUser = (user) => {
  if (!user) {
    throw new Error('Google sign-up succeeded, but the server did not return a user profile. Please try again.');
  }

  return {
    id: user._id || user.id || user.uid || '',
    fullName: user.name || user.displayName || '',
    email: user.email || '',
    avatar: user.photoURL || null,
    plan: user.plan || 'free',
    createdAt: user.createdAt,
    role: user.role || 'user',
    name: user.name || user.displayName || '',
    photoURL: user.photoURL || null,
    status: user.status || 'active',
  };
};

const Register = () => {
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [termsPromptVisible, setTermsPromptVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Tracks whether we are showing the "check your inbox" screen after sign-up
  const [pendingVerification, setPendingVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [signupOffer, setSignupOffer] = useState(null);

  // Public limited-offer banner (e.g. "First 100 get Gold FREE"). Silent fail — never blocks register.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiHelper.get('/plans/signup-offer');
        if (!cancelled && data?.offer) setSignupOffer(data.offer);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);
  const [resendLoading, setResendLoading] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const password = watch('password');
  const termsAccepted = watch('terms', false);
  const dispatch = useDispatch();

  const requireTerms = (event) => {
    if (!termsAccepted && !isLoading) {
      event.preventDefault();
      setTermsPromptVisible(true);
    }
  };

  const togglePassword = () => setShowPassword(s => !s);
  const toggleConfirmPassword = () => setShowConfirmPassword(s => !s);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Register on the backend — the server creates the MongoDB user,
      // generates a secure token, and sends the branded verification email.
      await apiHelper.post(API_ENDPOINTS.REGISTER, {
        email: data.email,
        password: data.password,
        name: data.name,
      });

      // Show the "check your inbox" screen
      setPendingEmail(data.email);
      setPendingVerification(true);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Registration failed. Please try again.';
      dispatch(loginFailure(message));
      showToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  // From the pending-verification screen, let the user request a new link.
  const handleResendFromPending = async () => {
    setResendLoading(true);
    try {
      await apiHelper.post(API_ENDPOINTS.RESEND_VERIFICATION, { email: pendingEmail });
      showToast('Verification email resent! Please check your inbox.');
    } catch (err) {
      const message = err.response?.data?.message || 'Could not resend. Please try again.';
      showToast(message);
    } finally {
      setResendLoading(false);
    }
  };

  const [deactivatedError, setDeactivatedError] = useState(null);
  const [sessionModal, setSessionModal] = useState({ open: false, sessions: [], email: '', password: '', cleanupToken: null });

  // ── Pending verification screen ─────────────────────────────────────────────
  // Shown after the user completes the registration form.  We do NOT log them
  // in until they click the Firebase verification link in their inbox.
  if (pendingVerification) {
    return (
      <div className="register">
        <div className="register__container">
          <div className="register__header">
            <div className="register__brand">
              <img src="/favi1.png" alt="MIVENT" className="register__logo-img" />
              <h1 className="register__logo">MIVENT</h1>
            </div>
            <p className="register__tagline">My Event Companion</p>
          </div>

          {/* Verification-pending notice */}
          <div className="register__verify-pending">
            <div className="register__verify-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Verify your email</h2>
            <p>
              We sent a verification link to <strong>{pendingEmail}</strong>.
              Please click the link in that email to activate your account.
            </p>
            <p className="register__verify-note">
              <strong>Important:</strong> Didn&apos;t receive the email? Please check your spam or junk folder. 
              If you still don&apos;t see it, you can request a new link below.
            </p>

            <button
              type="button"
              className="register__submit"
              onClick={handleResendFromPending}
              disabled={resendLoading}
            >
              {resendLoading ? 'Sending…' : 'Resend verification email'}
            </button>

            <div className="register__footer" style={{ marginTop: '1.5rem' }}>
              <p>
                Already verified?{' '}
                <Link to="/login" className="register__link">Sign In</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setDeactivatedError(null);
    try {
      const result = await handleGoogleAuth();
      const { token, user } = result;
      const appUser = buildAppUser(user);

      dispatch(loginStart());
      dispatch(loginSuccess({ user: appUser, token }));
      showToast('Account created with Google successfully!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Google sign up failed', err);
      const errorCode = err.response?.data?.code;
      const message = err.response?.data?.message || err.message || 'Google sign up failed. Please try again.';
      
      // Check if account is deactivated
      if (errorCode === 'ACCOUNT_DEACTIVATED') {
        setDeactivatedError(message);
        dispatch(loginFailure(message));
      } else if (errorCode === 'EMAIL_NOT_VERIFIED') {
        const email = err.response?.data?.email || '';
        setPendingEmail(email);
        setPendingVerification(true);
      } else if (errorCode === 'SESSION_LIMIT_REACHED') {
        dispatch(loginFailure(message));
        const sessions = err.response?.data?.sessions || [];
        const cleanupToken = err.response?.data?.cleanupToken || null;
        setSessionModal({ open: true, sessions, email: '', password: '', cleanupToken });
      } else {
        dispatch(loginFailure(message));
        showToast(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register">
      <div className="register__container">
        <div className="register__header">
          <div className="register__brand">
            <img src="/favi1.png" alt="MIVENT" className="register__logo-img" />
            <h1 className="register__logo">MIVENT</h1>
          </div>
          <p className="register__tagline">My Event Companion</p>
          <p className="register__subtitle">Create your account to get started</p>
          {signupOffer && (
            <div style={{
              background: signupOffer.isScheduled
                ? 'linear-gradient(135deg,#6366f1,#8b5cf6)'
                : 'linear-gradient(135deg,#f59e0b,#ef4444)',
              color: '#fff', borderRadius: 10, padding: '10px 14px',
              marginTop: 10, fontSize: 13, fontWeight: 600, textAlign: 'center',
            }}>
              {signupOffer.isScheduled ? '◷ ' : '🎁 '}{signupOffer.title || `First ${signupOffer.maxUsers} get ${String(signupOffer.plan).toUpperCase()} FREE`}
              <div style={{ fontWeight: 400, fontSize: 12, opacity: 0.95 }}>
                {signupOffer.isScheduled
                  ? `Starts ${signupOffer.startAt ? new Date(signupOffer.startAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'soon'}${signupOffer.validTill ? ` • ends ${new Date(signupOffer.validTill).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}`
                  : `${signupOffer.seatsLeft ?? '-'} seats left${signupOffer.validTill ? ` • ends ${new Date(signupOffer.validTill).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}${signupOffer.planValidTill ? ` • free till ${new Date(signupOffer.planValidTill).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : signupOffer.days ? ` • ${signupOffer.days} days free` : ''}`}
              </div>
            </div>
          )}
          <a
            className="register__tutorial-link"
            href="/support#tutorial"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Watch the MIVENT tutorial video"
          >
            What is MIVENT?
          </a>
        </div>

        {/* Session Manager Modal (shown when Google sign-up hits session limit) */}
        <SessionManagerModal
          isOpen={sessionModal.open}
          onClose={() => setSessionModal((s) => ({ ...s, open: false }))}
          sessions={sessionModal.sessions}
          email={sessionModal.email}
          password={sessionModal.password}
          cleanupToken={sessionModal.cleanupToken}
          onSessionRemoved={(id) =>
            setSessionModal((s) => ({
              ...s,
              sessions: s.sessions.filter((sess) => sess.id !== id),
            }))
          }
        />

        {/* Deactivated Account Warning */}
        {deactivatedError && (
          <div className="register__deactivated-warning">
            <div className="register__deactivated-icon">⚠️</div>
            <div className="register__deactivated-content">
              <h3>Account Terminated</h3>
              <p>{deactivatedError}</p>
            </div>
          </div>
        )}

        <form className="register__form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <div className="input-with-icon input-with-icon--leading">
              <User size={18} className="input-icon input-icon--leading" />
              <input
                id="name"
                type="text"
                placeholder="Enter your full name"
                {...register('name', { required: 'Name is required' })}
              />
            </div>
            {errors.name && <span className="error">{errors.name.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-with-icon input-with-icon--leading">
              <Mail size={18} className="input-icon input-icon--leading" />
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
            </div>
            {errors.email && <span className="error">{errors.email.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-with-icon">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a password"
                {...register('password', { 
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  },
                  pattern: {
                    value: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?=.{8,})/,
                    message: 'Password must include a capital letter, a number, and a special character'
                  }
                })}
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={togglePassword}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.58 10.58a3 3 0 004.24 4.24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.9 12.05C4.9 7.5 8.82 5 12 5c1.4 0 2.73.38 3.88 1.07" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14.12 15.88C12.98 16.5 11.52 16.5 10.38 15.88" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <span className="error">{errors.password.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="input-with-icon">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: value => value === password || 'Passwords do not match'
                })}
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={toggleConfirmPassword}
                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                title={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.58 10.58a3 3 0 004.24 4.24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.9 12.05C4.9 7.5 8.82 5 12 5c1.4 0 2.73.38 3.88 1.07" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14.12 15.88C12.98 16.5 11.52 16.5 10.38 15.88" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.confirmPassword && <span className="error">{errors.confirmPassword.message}</span>}
          </div>

          <div className="register__terms-toggle">
            <label className="switch">
              <input
                type="checkbox"
                {...register('terms', {
                  required: 'You must accept the terms and conditions'
                })}
              />
              <span className="slider" />
            </label>
            <span className="register__terms-text">
              I agree to the <button type="button" className="register__link" onClick={() => setShowTerms(true)}>Terms &amp; Conditions</button>
            </span>
          </div>
          {errors.terms && <span className="error register__terms-error">{errors.terms.message}</span>}
          {termsPromptVisible && !termsAccepted && !errors.terms && (
            <div className="register__terms-error" role="alert">
              Please agree to the Terms &amp; Conditions before continuing.
            </div>
          )}

          <button
            type="submit"
            className={`register__submit${!termsAccepted ? ' register__submit--terms-required' : ''}`}
            disabled={isLoading}
            aria-disabled={!termsAccepted}
            onClick={requireTerms}
          >
            {isLoading ? 'Creating Account...' : <><UserPlus size={18} /> Create Account</>}
          </button>

          <div className="register__divider">
            <span>or</span>
          </div>

          <button 
            type="button" 
            className={`register__google-btn${!termsAccepted ? ' register__google-btn--terms-required' : ''}`}
            onClick={(event) => {
              requireTerms(event);
              if (termsAccepted) handleGoogleSignUp();
            }}
            disabled={isLoading}
            aria-disabled={!termsAccepted}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {isLoading ? 'Creating Account...' : 'Sign up with Google'}
          </button>
        </form>

        <div className="register__footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="register__link">Sign In</Link>
          </p>
        </div>
        <TermsModal open={showTerms} onClose={() => setShowTerms(false)} />
      </div>
    </div>
  );
};

export default Register;
